import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { MapPinIcon } from './Icons/MapPinIcon'

/**
 * Notice card offering to convert a home-delivery order into Click & Collect.
 * Stateless and unconditional: the parent decides whether to render it
 * (via isCncUpsellEligible) and owns the navigation in `onClick`.
 *
 * Design decisions (see plan, 2026-09-06 reviews):
 * - Outline button, not filled: the delivery date stays the page's hero.
 * - Button label reuses showOrder.pick_up_nearby so card, bottom link and
 *   destination page title share one name.
 * - hover:!bg-blue-100 overrides the outline variant's hover:bg-blue-50, which
 *   matches this card's own bg-blue-50 and would make the button dissolve on
 *   hover. The `!` (important) modifier is required: Tailwind emits the blue-100
 *   hover rule BEFORE the blue-50 one, so without it the variant's rule wins at
 *   equal specificity (verified in Chromium against the compiled stylesheet).
 */
export function CncUpsellCard({ onClick }) {
    const { t } = useTranslation()

    return (
        <div
            className="p-4 bg-blue-50 border border-blue-200 rounded-md flex flex-col gap-3"
            role="region"
            aria-label={t('cncUpsellCard.regionLabel')}
        >
            <div className="flex items-start gap-3">
                <MapPinIcon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{t('cncUpsellCard.headline')}</div>
                    <div className="text-sm text-gray-700">{t('cncUpsellCard.subline')}</div>
                </div>
            </div>
            <Button
                variant="outline"
                size="md"
                className="hover:!bg-blue-100"
                label={t('showOrder.pick_up_nearby')}
                onClick={onClick}
            />
        </div>
    )
}
